import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FileText,
  History,
  Lock,
  Search,
  Users,
  Shield,
  ArrowRight,
  GitBranch,
  Settings,
  Eye,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileCheck,
  TrendingUp
} from 'lucide-react';

// Reusable components
const MetricCard = ({ icon: Icon, value, label, trend }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="flex flex-col items-center gap-4 mb-4">
      <div className="p-3 rounded-lg bg-indigo-500/10">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
      {trend && (
        <div className="bg-green-500/10 px-2 py-1 rounded-full flex items-center gap-1">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-sm">{trend}</span>
        </div>
      )}
    </div>
    <div className="text-3xl font-bold text-white mb-2">{value}</div>
    <p className="text-gray-400">{label}</p>
  </motion.div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
    <motion.div
      className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors flex flex-col items-center text-center"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </motion.div>
);

const DocumentControl = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-indigo-900 to-purple-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 text-sm text-indigo-400 bg-indigo-500/10 rounded-full px-4 py-1.5 border border-indigo-500/20">
                <FileText className="w-4 h-4" />
                Enterprise Document Control
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Secure Document Management at Scale
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Enterprise-grade document control system with advanced versioning,
              compliance tracking, and real-time collaboration capabilities.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              icon={FileCheck}
              value="50M+"
              label="Documents Managed"
              trend="+25%"
            />
            <MetricCard
              icon={Clock}
              value="99.99%"
              label="System Availability"
            />
            <MetricCard
              icon={Shield}
              value="ISO 27001"
              label="Security Certified"
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Enterprise Document Control Features
            </h2>
            <p className="text-gray-300">
              Comprehensive tools for managing documents at global scale
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={History}
              title="Version Control"
              description="Advanced versioning with branching, merging, and complete audit history tracking."
            />
            <FeatureCard
              icon={Lock}
              title="Access Control"
              description="Granular permission management with role-based access control and SSO integration."
            />
            <FeatureCard
              icon={Search}
              title="Smart Search"
              description="AI-powered document search with OCR and content analysis capabilities."
            />
            <FeatureCard
              icon={Users}
              title="Collaboration"
              description="Real-time collaborative editing with conflict resolution and change tracking."
            />
            <FeatureCard
              icon={AlertTriangle}
              title="Compliance"
              description="Automated compliance checking and regulatory requirement validation."
            />
            <FeatureCard
              icon={GitBranch}
              title="Workflow Automation"
              description="Customizable approval workflows and document lifecycle management."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Document Management?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of enterprises managing millions of documents securely
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Get Started Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DocumentControl;